import type { routes, SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { serviceCenterStore } from 'App/ServiceCenter/store/serviceCenterStore'
import type { TActiveNode, TNodeTrap } from 'App/ServiceCenter/types'
import { cloneDeep, isEmpty } from 'lodash'
import { apiFetch } from 'utils/apiFetch'
import { isTrapNode } from '../constants/nodeType'
import { getIdentifier } from '../getIdentifier'

export type NodeConfigUpdateMeta = routes.ServiceCenterNodeConfigUpdate.Request

const request = async (payload: routes.ServiceCenterNodeConfigUpdate.Request) => {
  await apiFetch({
    url: '/service-center-node-config-update',
    body: payload,
    params: {
      method: 'PUT',
    },
  })

  return payload
}

const callback = (meta: NodeConfigUpdateMeta): void => {
  const {
    nodeIdentifier: nodeIdentifierToUpdate,
    trap: { targetInsectId, cameraOrientation, lureIds, consumableTypes, consumableOperationType } = {},
  } = meta

  const activeNodes = serviceCenterStore.selectors.getActiveNodes(serviceCenterStore.getState())

  let nodeToUpdate = cloneDeep(activeNodes.find((node) => getIdentifier(node) === nodeIdentifierToUpdate))

  if (!nodeToUpdate) return

  // trap update
  if (isTrapNode((nodeToUpdate as TActiveNode).nodeType)) {
    //@ts-ignore - trap is not defined when drop installing a node
    if (!nodeToUpdate.trap) nodeToUpdate.trap = {}

    if (cameraOrientation) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      nodeToUpdate.trap!.cameraOrientation = cameraOrientation
    }

    const nodeTrap = nodeToUpdate?.trap as TNodeTrap

    if (consumableTypes) {
      if (!nodeTrap.lastService || isEmpty(nodeTrap.lastService)) {
        nodeTrap.lastService = {
          batteryServicedLastOn: null,
          coroplastServicedLastOn: null,
          issueServicedLastOn: null,
          linerServicedLastOn: null,
          lureServicedLastOn: null,
        }
      }

      const now = new Date().toISOString()

      if (consumableOperationType === 'SET') {
        if (consumableTypes.includes('LURE') && lureIds) {
          const updatedLures: SharedTypes.ServiceCenter.TNodeLures['active'] = {}

          lureIds.forEach((lureId, i) => {
            updatedLures[lureId] = {
              lureId,
              primary: i === 0,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              maintenanceOwnerId: (nodeToUpdate as TActiveNode).maintenanceOwnerId!,
              activeStart: now,
            }
          })

          nodeTrap.lures = {
            active: updatedLures,
          }

          nodeTrap.lastService.lureServicedLastOn = now
        }

        if (consumableTypes.includes('LINER')) {
          nodeTrap.liner = {
            activeStart: now,
            maintenanceOwnerId:
              nodeTrap.liner?.maintenanceOwnerId ||
              ((nodeToUpdate as TActiveNode).maintenanceOwnerId as number),
          }

          nodeTrap.lastService.linerServicedLastOn = now
        }

        if (consumableTypes.includes('COROPLAST')) {
          nodeTrap.coroplast = {
            activeStart: now,
          }

          nodeTrap.lastService.coroplastServicedLastOn = now
        }

        if (consumableTypes.includes('BATTERY')) {
          nodeTrap.battery = {
            activeStart: now,
          }

          nodeTrap.lastService.batteryServicedLastOn = now
        }
      } else if (consumableOperationType === 'REMOVE') {
        if (consumableTypes.includes('LURE')) {
          nodeTrap.lures = {
            active: {},
          }

          nodeTrap.lastService.lureServicedLastOn = now
        }

        if (consumableTypes.includes('LINER')) {
          nodeTrap.liner = {
            activeStart: null,
            maintenanceOwnerId:
              nodeTrap.liner?.maintenanceOwnerId ||
              ((nodeToUpdate as TActiveNode).maintenanceOwnerId as number),
          }

          nodeTrap.lastService.linerServicedLastOn = now
        }

        if (consumableTypes.includes('COROPLAST')) {
          nodeTrap.coroplast = {
            activeStart: null,
          }

          nodeTrap.lastService.coroplastServicedLastOn = now
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    nodeToUpdate.trap!.targetInsectId = targetInsectId || null
  }

  serviceCenterStore.actions.updateActiveNode(nodeToUpdate)
}

export const serviceCenterNodeConfigSet = {
  request,
  callback,
}
